import {
  EMPTY_SPEAKER_NAME_INDEX,
  resolveDialogueSpeaker,
  type SpeakerNameIndex,
} from './speakerToken'

export interface MotionSegment {
  type: 'action' | 'dialogue' | 'voiceover'
  timestamp: string
  startTime: number
  endTime: number
  action: string
  camera: string | null
  character?: string
  emotion?: string
}

export interface ParsedMotionPrompt {
  clipHeader: string | null
  segments: MotionSegment[]
  fallbackText: string | null
  /** Clip-wide content after the last timestamped segment, introduced by a tag
   * this parser does not own (e.g. the `<voices>` roster). Preserved verbatim
   * instead of being absorbed into the preceding segment's body. */
  trailingText: string | null
}

/** Tags whose bodies contain timestamped segments. Any other tag ends segment
 * parsing and starts `trailingText`. */
const SEGMENT_TAGS = new Set(['dialogue', 'voiceover'])

/** Extract numeric start time from a timestamp like "1.0s-3.5s" */
export function parseStartTime(ts: string): number {
  const match = ts.match(/^(\d+(?:\.\d+)?)s?/)
  return match ? parseFloat(match[1]) : 0
}

function parseTimestampRange(ts: string): { start: number; end: number } {
  const match = ts.match(/^(\d+(?:\.\d+)?)s?-(\d+(?:\.\d+)?)s?$/)
  if (!match) return { start: parseStartTime(ts), end: parseStartTime(ts) }
  return { start: parseFloat(match[1]), end: parseFloat(match[2]) }
}

/** Parse timestamped segments from a text block.
 *
 * `knownSpeakerIndex` carries the connected voice-profile names so a
 * comma-bearing speaker resolves to the real profile instead of being truncated
 * at the first comma (RFC-1203 / RFC-1204). Omit it and parsing is exactly the
 * legacy first-comma split, which is what every caller without profile context
 * (and every existing test) gets.
 */
export function parseTimestampedSegments(
  block: string,
  segType: 'action' | 'dialogue' | 'voiceover',
  knownSpeakerIndex: SpeakerNameIndex = EMPTY_SPEAKER_NAME_INDEX,
): MotionSegment[] {
  const timestampRegex = /\[(\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s)\]:?\s*/g
  const matches = [...block.matchAll(timestampRegex)]
  if (matches.length === 0) return []

  return matches.map((match, i) => {
    const start = match.index! + match[0].length
    const end = i < matches.length - 1 ? matches[i + 1].index! : block.length
    let segmentText = block.slice(start, end).trim()

    let camera: string | null = null
    let character: string | undefined
    let emotion: string | undefined

    if (segType === 'action') {
      const cameraMatch = segmentText.match(/Camera:\s*(.+?)$/i)
      if (cameraMatch) {
        camera = cameraMatch[1].trim()
        segmentText = segmentText.slice(0, cameraMatch.index).trim()
      }
    } else if (segType === 'dialogue' || segType === 'voiceover') {
      // New format: [@Character,emotion]:"text"
      const speakerMatch = segmentText.match(/^\[@([^\]]*)\]:?/)
      if (speakerMatch) {
        // A connected profile name may itself contain a comma, so the speaker /
        // emotion boundary cannot be found by string-splitting alone.
        const resolved = resolveDialogueSpeaker(speakerMatch[1], knownSpeakerIndex)
        character = resolved.character
        emotion = resolved.emotion || undefined
        segmentText = segmentText.slice(speakerMatch[0].length).trim()
      } else {
        // Backward compat: old format "Character: \"text\""
        const colonIdx = segmentText.indexOf(':')
        if (colonIdx > 0) {
          character = segmentText.slice(0, colonIdx).trim()
          segmentText = segmentText.slice(colonIdx + 1).trim()
        }
      }
      // Strip surrounding ASCII/smart quotes regardless of speaker form, so
      // bare `[Ns-Ns]: ""` lines parse to empty text instead of literal `""`.
      segmentText = segmentText.replace(/^[""\u201c]+|[""\u201d]+$/g, '').trim()
    }

    const range = parseTimestampRange(match[1])
    return {
      type: segType,
      timestamp: match[1],
      startTime: range.start,
      endTime: range.end,
      action: segmentText,
      camera,
      character,
      emotion,
    }
  })
}

export function parseMotionPrompt(
  text: string,
  knownSpeakerIndex: SpeakerNameIndex = EMPTY_SPEAKER_NAME_INDEX,
): ParsedMotionPrompt {
  if (!text.trim()) {
    return { clipHeader: null, segments: [], fallbackText: null, trailingText: null }
  }

  // Extract tagged blocks and the leading action block. Tags can appear in any
  // order; everything before the first tag is the action block. The first tag
  // this parser does not own ends segment parsing — everything from there on is
  // trailing content, so a `<voices>` roster is not swallowed by the last
  // dialogue line and re-serialized inside its quotes.
  const tagRegex = /<([a-z_]+)>\n?/gi
  const allTagMatches = [...text.matchAll(tagRegex)]

  const firstForeignTag = allTagMatches.find((m) => !SEGMENT_TAGS.has(m[1].toLowerCase()))
  const parseLimit = firstForeignTag ? firstForeignTag.index! : text.length
  // Reported verbatim. Formatting is the serializer's decision, not the
  // parser's, so a prompt the user never touched does not come back changed.
  const trailingText = firstForeignTag ? text.slice(parseLimit) : null
  const tagMatches = allTagMatches.filter((m) => m.index! < parseLimit)

  const actionBlock = tagMatches.length > 0 ? text.slice(0, tagMatches[0].index) : text.slice(0, parseLimit)

  const taggedBlocks: Array<{ type: 'dialogue' | 'voiceover'; content: string }> = []
  for (let i = 0; i < tagMatches.length; i++) {
    const start = tagMatches[i].index! + tagMatches[i][0].length
    const end = i < tagMatches.length - 1 ? tagMatches[i + 1].index! : parseLimit
    const blockType = tagMatches[i][1].toLowerCase() === 'dialogue' ? 'dialogue' : 'voiceover'
    taggedBlocks.push({
      type: blockType,
      content: text.slice(start, end),
    })
  }

  const actionSegments = parseTimestampedSegments(actionBlock, 'action')
  const allTaggedSegments = taggedBlocks.flatMap(b =>
    parseTimestampedSegments(b.content, b.type, knownSpeakerIndex),
  )

  if (actionSegments.length === 0 && allTaggedSegments.length === 0) {
    return { clipHeader: null, segments: [], fallbackText: text, trailingText: null }
  }

  const firstTsMatch = actionBlock.match(/\[\d+(?:\.\d+)?s?-\d+(?:\.\d+)?s\]/)
  const clipHeader = firstTsMatch
    ? actionBlock.slice(0, firstTsMatch.index).replace(/:\s*$/, '').trim() || null
    : null

  const segments = [...actionSegments, ...allTaggedSegments].sort(
    (a, b) => a.startTime - b.startTime,
  )

  return { clipHeader, segments, fallbackText: null, trailingText }
}
